import { useEffect, useState } from 'react';
import { Plus, Truck, MoreHorizontal, Eye, MapPin, X, Package, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  StatusBadge,
} from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import {
  shipmentsApi,
  Shipment,
  ShipmentStatus,
  QueryShipmentParams,
  locationsApi,
  Location,
  inventoryApi,
  InventoryItem,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';

const statusMap: Record<string, 'draft' | 'booked' | 'in_transit' | 'delivered' | 'delayed' | 'cancelled'> = {
  DRAFT: 'draft',
  BOOKED: 'booked',
  IN_TRANSIT: 'in_transit',
  DELIVERED: 'delivered',
  DELAYED: 'delayed',
  CANCELLED: 'cancelled',
};

interface ShipmentLineItem {
  itemId: string;
  item?: InventoryItem;
  quantity: number;
}

interface ShipmentFormData {
  originId: string;
  destinationId: string;
  carrier: string;
  trackingNumber: string;
  estimatedDeparture: string;
  estimatedArrival: string;
  totalWeight: string;
  totalVolume: string;
  items: ShipmentLineItem[];
}

const emptyFormData: ShipmentFormData = {
  originId: '',
  destinationId: '',
  carrier: '',
  trackingNumber: '',
  estimatedDeparture: '',
  estimatedArrival: '',
  totalWeight: '',
  totalVolume: '',
  items: [],
};

export default function Shipments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [formData, setFormData] = useState<ShipmentFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  // Reference data
  const [locations, setLocations] = useState<Location[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loadingReferences, setLoadingReferences] = useState(false);

  // Bulk status update state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<ShipmentStatus | ''>('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const fetchShipments = async (params?: QueryShipmentParams) => {
    try {
      setLoading(true);
      const response = await shipmentsApi.getAll(params);
      setShipments(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      setLoadingReferences(true);
      const [locationsRes, itemsRes] = await Promise.all([
        locationsApi.getAll({ isActive: true }),
        inventoryApi.getAll(),
      ]);
      setLocations(locationsRes.data);
      setInventoryItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to load reference data');
    } finally {
      setLoadingReferences(false);
    }
  };

  const handleOpenSheet = () => {
    setFormData(emptyFormData);
    fetchReferenceData();
    setIsSheetOpen(true);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemId: '', quantity: 1 }],
    });
  };

  const handleRemoveItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const handleItemChange = (index: number, field: keyof ShipmentLineItem, value: string | number) => {
    const newItems = [...formData.items];
    if (field === 'itemId') {
      const selectedItem = inventoryItems.find((i) => i.id === value);
      newItems[index] = { ...newItems[index], itemId: value as string, item: selectedItem };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleSave = async () => {
    if (!formData.originId) {
      toast.error('Please select an origin location');
      return;
    }
    if (!formData.destinationId) {
      toast.error('Please select a destination location');
      return;
    }
    if (formData.originId === formData.destinationId) {
      toast.error('Origin and destination must be different');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    for (const item of formData.items) {
      if (!item.itemId || item.quantity <= 0) {
        toast.error('All items must have a selected item and quantity greater than 0');
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        companyId: user?.companyId || '',
        originId: formData.originId,
        destinationId: formData.destinationId,
        carrier: formData.carrier || undefined,
        trackingNumber: formData.trackingNumber || undefined,
        estimatedDeparture: formData.estimatedDeparture || undefined,
        estimatedArrival: formData.estimatedArrival || undefined,
        totalWeight: formData.totalWeight ? parseFloat(formData.totalWeight) : undefined,
        totalVolume: formData.totalVolume ? parseFloat(formData.totalVolume) : undefined,
        items: formData.items.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
        })),
      };

      await shipmentsApi.create(payload);
      toast.success('Shipment created successfully');
      setIsSheetOpen(false);
      fetchShipments();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // Bulk status update handlers
  const handleOpenBulkStatus = () => {
    setBulkStatus('');
    setBulkStatusOpen(true);
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.length === 0) return;

    try {
      setBulkUpdating(true);
      let success = 0;
      let failed = 0;

      for (const id of selectedIds) {
        try {
          await shipmentsApi.updateStatus(id, { status: bulkStatus });
          success++;
        } catch {
          failed++;
        }
      }

      if (success > 0) {
        toast.success(`Updated ${success} shipment${success > 1 ? 's' : ''} successfully`);
        fetchShipments();
      }
      if (failed > 0) {
        toast.error(`Failed to update ${failed} shipment${failed > 1 ? 's' : ''}`);
      }

      setBulkStatusOpen(false);
      setSelectedIds([]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBulkUpdating(false);
    }
  };

  useEffect(() => {
    const params: QueryShipmentParams = {
      search: searchQuery || undefined,
    };
    if (statusFilter !== 'all') {
      params.status = statusFilter as Shipment['status'];
    }
    fetchShipments(params);
  }, [searchQuery, statusFilter]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const columns: Column<Shipment>[] = [
    {
      key: 'shipmentNumber',
      header: 'Shipment',
      sortable: true,
      render: (shipment) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Truck className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{shipment.shipmentNumber}</div>
            {shipment.trackingNumber && (
              <div className="text-sm text-muted-foreground">
                {shipment.trackingNumber}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      render: (shipment) => (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{shipment.origin?.code || '-'}</span>
          <span className="text-muted-foreground">→</span>
          <span className="font-medium">{shipment.destination?.code || '-'}</span>
        </div>
      ),
    },
    {
      key: 'carrier',
      header: 'Carrier',
      render: (shipment) => (
        <span className="text-muted-foreground">{shipment.carrier || '-'}</span>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (shipment) => (
        <Badge variant="outline">{shipment._count?.items || 0}</Badge>
      ),
    },
    {
      key: 'dates',
      header: 'Est. Arrival',
      render: (shipment) => (
        <span className="text-muted-foreground">
          {formatDate(shipment.estimatedArrival)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (shipment) => (
        <StatusBadge status={statusMap[shipment.status] || 'draft'} />
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (shipment) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/shipments/${shipment.id}`)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem>
              <MapPin className="mr-2 h-4 w-4" />
              Track
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader
        title="Shipments"
        description="Track and manage shipments across your supply chain"
        breadcrumbs={[{ label: 'Shipments' }]}
        actions={
          <div className="flex gap-2">
            {selectedIds.length > 0 && (
              <Button variant="outline" onClick={handleOpenBulkStatus}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Update Status ({selectedIds.length})
              </Button>
            )}
            <Button onClick={handleOpenSheet}>
              <Plus className="mr-2 h-4 w-4" />
              Create Shipment
            </Button>
          </div>
        }
      />

      {/* Bulk selection info bar */}
      {selectedIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-primary/10 px-4 py-2">
          <span className="text-sm">
            <strong>{selectedIds.length}</strong> shipment{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            Clear selection
          </Button>
        </div>
      )}

      <div className="mb-4 flex items-center gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search shipments..."
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="BOOKED">Booked</SelectItem>
            <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
            <SelectItem value="DELIVERED">Delivered</SelectItem>
            <SelectItem value="DELAYED">Delayed</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={shipments}
        columns={columns}
        loading={loading}
        onRowClick={(shipment) => navigate(`/shipments/${shipment.id}`)}
        emptyTitle="No shipments found"
        emptyDescription="Get started by creating your first shipment."
        emptyAction={
          <Button onClick={handleOpenSheet}>
            <Plus className="mr-2 h-4 w-4" />
            Create Shipment
          </Button>
        }
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(shipment) => shipment.id}
      />

      {/* Create Shipment Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Shipment</SheetTitle>
            <SheetDescription>
              Create a new shipment to transfer items between locations.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Route */}
            <div className="space-y-4">
              <h4 className="font-medium">Route</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="originId">Origin *</Label>
                  <Select
                    value={formData.originId}
                    onValueChange={(value) => setFormData({ ...formData, originId: value })}
                    disabled={loadingReferences}
                  >
                    <SelectTrigger id="originId">
                      <SelectValue placeholder="Select origin" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name} ({location.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="destinationId">Destination *</Label>
                  <Select
                    value={formData.destinationId}
                    onValueChange={(value) => setFormData({ ...formData, destinationId: value })}
                    disabled={loadingReferences}
                  >
                    <SelectTrigger id="destinationId">
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name} ({location.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Carrier & Tracking */}
            <div className="space-y-4">
              <h4 className="font-medium">Carrier Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="carrier">Carrier</Label>
                  <Input
                    id="carrier"
                    value={formData.carrier}
                    onChange={(e) => setFormData({ ...formData, carrier: e.target.value })}
                    placeholder="e.g., FedEx, UPS"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trackingNumber">Tracking Number</Label>
                  <Input
                    id="trackingNumber"
                    value={formData.trackingNumber}
                    onChange={(e) => setFormData({ ...formData, trackingNumber: e.target.value })}
                    placeholder="Enter tracking number"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Dates */}
            <div className="space-y-4">
              <h4 className="font-medium">Schedule</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estimatedDeparture">Est. Departure</Label>
                  <Input
                    id="estimatedDeparture"
                    type="date"
                    value={formData.estimatedDeparture}
                    onChange={(e) => setFormData({ ...formData, estimatedDeparture: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estimatedArrival">Est. Arrival</Label>
                  <Input
                    id="estimatedArrival"
                    type="date"
                    value={formData.estimatedArrival}
                    onChange={(e) => setFormData({ ...formData, estimatedArrival: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Weight & Volume */}
            <div className="space-y-4">
              <h4 className="font-medium">Dimensions (Optional)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="totalWeight">Total Weight (kg)</Label>
                  <Input
                    id="totalWeight"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.totalWeight}
                    onChange={(e) => setFormData({ ...formData, totalWeight: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalVolume">Total Volume (m³)</Label>
                  <Input
                    id="totalVolume"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.totalVolume}
                    onChange={(e) => setFormData({ ...formData, totalVolume: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Shipment Items *</h4>
                <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Item
                </Button>
              </div>

              {formData.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-6 text-center">
                  <Package className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No items added yet.</p>
                  <Button type="button" variant="outline" size="sm" className="mt-2" onClick={handleAddItem}>
                    Add First Item
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-lg border p-3"
                    >
                      <div className="flex-1 space-y-3">
                        <div className="space-y-2">
                          <Label>Item</Label>
                          <Select
                            value={item.itemId}
                            onValueChange={(value) => handleItemChange(index, 'itemId', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select item" />
                            </SelectTrigger>
                            <SelectContent>
                              {inventoryItems.map((invItem) => (
                                <SelectItem key={invItem.id} value={invItem.id}>
                                  {invItem.name} ({invItem.sku})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)
                            }
                          />
                        </div>
                        {item.item && (
                          <p className="text-sm text-muted-foreground">
                            Unit: {item.item.unit}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsSheetOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Creating...' : 'Create Shipment'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Bulk Status Update Dialog */}
      <Dialog open={bulkStatusOpen} onOpenChange={setBulkStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Shipment Status</DialogTitle>
            <DialogDescription>
              Change the status of {selectedIds.length} selected shipment{selectedIds.length > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="bulkStatus">New Status</Label>
            <Select
              value={bulkStatus}
              onValueChange={(value) => setBulkStatus(value as ShipmentStatus)}
            >
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="BOOKED">Booked</SelectItem>
                <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
                <SelectItem value="DELAYED">Delayed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkStatusUpdate}
              disabled={!bulkStatus || bulkUpdating}
            >
              {bulkUpdating ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
